import type { LucideIcon } from "lucide-react";
import {
  Code2,
  Cpu,
  RadioTower,
  CircuitBoard,
  Factory,
  Bot,
  ShieldCheck,
} from "lucide-react";

export interface Capability {
  icon: LucideIcon;
  title: string;
  tag: string;
  body: string;
}

export const CAPABILITIES: Capability[] = [
  {
    icon: Code2,
    title: "Software Engineering",
    tag: "full-stack · cloud-native",
    body: "Production systems end to end — APIs, data layers, and the autonomous platforms on top. Built to run reliably at scale and shipped through real delivery pipelines, not thrown over a wall.",
  },
  {
    icon: Cpu,
    title: "Embedded Systems",
    tag: "firmware · RTOS · bare-metal",
    body: "Firmware from bring-up to over-the-air update. Sensor fusion, low-power design, and on-device intelligence squeezed onto constrained hardware — where the silicon, not the cloud, has to make the call.",
  },
  {
    icon: RadioTower,
    title: "IoT & Edge",
    tag: "fleets · telemetry · OTA",
    body: "Connected device fleets that survive the field: edge telemetry, secure provisioning, remote update, and the back-end pipelines that turn millions of readings into something a team can act on.",
  },
  {
    icon: CircuitBoard,
    title: "Electrical Engineering",
    tag: "schematic · PCB · power",
    body: "Hardware that ships — schematic capture, board layout, power and signal integrity, and bring-up — so the firmware lands on something that was designed to run it.",
  },
  {
    icon: Factory,
    title: "Embedded Manufacturing",
    tag: "MES · IC fab · assembly · yield",
    body: "Standing up the line itself. MES development that tracks every unit through the floor, plus the manufacturing process for small ICs and embedded systems — fabrication, assembly, programming, test, and yield.",
  },
  {
    icon: Bot,
    title: "AI & Autonomous Agents",
    tag: "agents · orchestration · tool-use",
    body: "Multi-agent systems that do real work: routing, tool-use loops, and approval gates. Frontier models where they pay off, self-hosted intelligence where sovereignty and latency demand it.",
  },
  {
    icon: ShieldCheck,
    title: "Security & Air-Gapped",
    tag: "hardened · sovereign · gov",
    body: "Air-gapped, AI-empowered devices and locked-down infrastructure with a security-first posture — signed identity, allow-listed surfaces, and systems built to a standard government use demands.",
  },
];

export interface Product {
  id: string;
  name: string;
  glyph: string;
  tagline: string;
  summary: string;
  body: string;
  points: string[];
  accent: "signal" | "invert";
}

export const PRODUCTS: Product[] = [
  {
    id: "carcin",
    name: "Carcin",
    glyph: "01",
    tagline: "AI operator for small business",
    summary:
      "An autonomous operator that finds a business's most painful recurring work and stands up approval-gated AI workflows to run it end to end.",
    body:
      "Carcin connects a client's marketing, comms, and lead channels, then delegates the busywork — audits, ad campaigns, content, review responses, inbox triage — to AI agents. It keeps persistent account memory across sessions and never touches the outside world without a human approving the action first. Multi-tenant and cloud-native.",
    points: [
      "Conversational discovery instead of rigid setup wizards",
      "Human-in-the-loop action queue before any external write",
      "Per-tenant isolation, semantic search over uploaded documents",
    ],
    accent: "signal",
  },
  {
    id: "crucible",
    name: "Crucible",
    glyph: "02",
    tagline: "The agent foundry — forge · delegate · deploy",
    summary:
      "An autonomous software factory: a fleet of specialized AI agents that build, review, and ship software with a human at the helm, not in the loop.",
    body:
      "A signal — a code-repository event, a backlog item, a message — is scored by a router and dispatched to the best-matched specialist agent. Agents propose changes, a lead agent reviews and merges, and the pipeline ships to production. Inference runs self-hosted on local hardware with no third-party dependency, behind a locked-down edge, with signed device identity and verified events.",
    points: [
      "Capability-scored router over a fleet of specialist agents",
      "Self-hosted inference — sovereign, no data leaves the box",
      "Signed identity + allow-listed surface; secrets never exposed",
    ],
    accent: "invert",
  },
  {
    id: "narsil",
    name: "Narsil",
    glyph: "03",
    tagline: "Agent-driven delivery — the reforged blade",
    summary:
      "A delivery pipeline where each stage is run by an AI agent or a deterministic script: adaptive where it helps, deterministic where it must be.",
    body:
      "Pipelines are defined in plain config; stages execute through pluggable adapters — a deterministic script, an AI agent, or a custom agent — bounded by per-stage capability allow-lists and hard turn caps so autonomy never runs away. Narsil persists run state, posts summaries to chat, and serves a live dashboard. Determinism first; agency is opt-in.",
    points: [
      "Per-stage capability allow-lists + max-turn caps on every agent",
      "Pluggable adapters: script · AI · custom — no model lock-in",
      "Durable run state with a live, streaming dashboard",
    ],
    accent: "signal",
  },
];
